import React, { useEffect, useState, useCallback } from "react";
import { Button, Modal, Space, Table, TableProps, Tag, Input, message } from "antd";
import { DDRDisplayData } from "../../types/DDRDisplayData";
import { reportStateDescription } from "../../utils/Enum";
import { fetchStagingPlodsByAid, updateStagingPlodDDRState } from "../../backendInteraction/ApiFetch";
import { StagingTitelineAppPlod } from "../../types/StagingTitelineAppPlod";
import { useNavigate } from "react-router-dom";
import notLoginState from "../../asserts/notLoginState.png";
import './DDR2ApprovePage.css';
import { fetchTotalActivityHours } from "../../backendInteraction/ActivitiesApiFetch";
import { addRejectLog, fetchLatestLogMessageByPid } from "../../backendInteraction/DDRRejectLogApiFetch";
import { StagingTitelineAppDDRRejectLog } from "../../types/StagingTitelineAppDDRRejectLog";

export const DDR2ApprovePage = () => {
    const [aid, setAid] = useState<number>(() => {
        const storedUser = localStorage.getItem("DORS_USER");
        return storedUser ? JSON.parse(storedUser).Aid : 0;
    });

    const [authorityLv, setAuthorityLv] = useState<string>(() => {
        const storedUser = localStorage.getItem("DORS_USER");
        return storedUser ? JSON.parse(storedUser).AuthorityLv : '';
    });

    const [data, setData] = useState<DDRDisplayData[] | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isApproveAction, setIsApproveAction] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedPid, setSelectedPid] = useState<number | null>(null);
    const [rejectionMessage, setRejectionMessage] = useState<string>('');


    const { TextArea } = Input;

    const navigate = useNavigate();

    const transformPlodsToDDRDisplay = async (plods: StagingTitelineAppPlod[]): Promise<DDRDisplayData[]> => {
        return await Promise.all(plods.map(async (plod) => {
            const safeString = (value: any) => (!value || value === 'null' ? '' : value);

            const date = safeString(plod.PlodDate);
            const shift = safeString(plod.PlodShift);
            const creation_date = date && shift ? `${date}-${shift}` : '';

            let totalHours: number = 0;
            const rawHours = plod.TotalActivityHrs;

            if (isNaN(Number(rawHours)) || Number(rawHours) == 0) {
                totalHours = await fetchTotalActivityHours(plod.Pid ?? -1);
            } else {
                totalHours = parseFloat(Number(rawHours).toFixed(2));
            }

            return {
                key: plod.Pid,
                pid: plod.Pid,
                contract_no: safeString(plod.ContractNo),
                date,
                shift,
                creation_date,
                rig_no: safeString(plod.RigNo),
                report_state: parseInt(plod.ReportState) || -1,
                department: safeString(plod.Department),
                location: safeString(plod.Location),
                mach_hrs_from: safeString(plod.MachineHoursFrom),
                mach_hrs_to: safeString(plod.MachineHoursTo),
                total_activity_hours: totalHours,
                comments: safeString(plod.Comments),
            };
        }));
    };

    const loadData = useCallback(async () => {
        try {
            const fetchedData = await fetchStagingPlodsByAid(aid, true);
            if (fetchedData) {
                const transformedData = await transformPlodsToDDRDisplay(fetchedData);
                setData(transformedData);
            } else {
                console.log('No data available');
            }
        } catch (error) {
            console.error('Error loading data from cloud server:', error);
        }
    }, [aid]);

    const rejectMsgOnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRejectionMessage(e.target.value);
    };

    const handleCancel = () => {
        setRejectionMessage("");
        setIsModalOpen(false);
        setSelectedPid(null);
    };

    const handleOk = async () => {
        if (selectedPid === null || isSubmitting) return;

        setIsSubmitting(true);

        try {
            const updated = await updateStagingPlodDDRState(selectedPid, isApproveAction);
            if (!updated) {
                message.error("Failed to update DDR state.");
                handleCancel();
                return;
            }

            if (!isApproveAction) {
                const rejectionLog: StagingTitelineAppDDRRejectLog = {
                    Pid: selectedPid,
                    RejectedBy: aid,
                    CreationDateTime: new Date().toISOString(),
                    Message: rejectionMessage
                };

                const success = await addRejectLog(rejectionLog);

                if (!success) {
                    message.error("Failed to save rejection log.");
                    handleCancel();
                    return;
                }

                message.success("Rejection log submitted successfully.");
            } else {
                message.success("DDR approved successfully.");
            }

            await loadData();

        } catch (error) {
            console.error("Error in handleOk:", error);
            message.error("An unexpected error occurred.");
        } finally {
            setIsSubmitting(false);
            handleCancel();
        }
    };

    const passDDR2Next = (pid: number, assessment: boolean) => {
        setSelectedPid(pid);
        setIsApproveAction(assessment);
        setIsModalOpen(true);
    };

    const handleEditing = (record: DDRDisplayData) => {
        navigate("/view-ddr", { state: { ddrData: record } });
    };

    useEffect(() => {
        if (aid !== 0) {
            loadData();
        }
    }, [aid, loadData]);

    const viewFeedback = async (pid: number) => {
        const messageText = await fetchLatestLogMessageByPid(pid, aid);
        Modal.info({
            title: 'Rejection Feedback',
            content: <p>{messageText}</p>,
            okText: 'Close'
        });
    };

    const columns: TableProps<DDRDisplayData>['columns'] = [
        {
            title: 'Contract Number',
            dataIndex: 'contract_no',
            key: 'contract_no',
            render: (text) => <span className="contract_no_text">{text}</span>,
        },
        {
            title: 'Plod Date',
            dataIndex: 'creation_date',
            key: 'creation_date',
        },
        {
            title: 'Rig Number',
            dataIndex: 'rig_no',
            key: 'rig_no',
        },
        {
            title: 'Report State',
            key: 'report_state',
            dataIndex: 'report_state',
            render: (state: number) => (
                <Tag color={(state < 0 || state > 5) ? 'default' : state % 2 === 0 ? 'orange' : 'green'}>
                    {reportStateDescription[state] || "Unknown State"}
                </Tag>
            ),
        },
        {
            title: 'Action',
            key: 'action',
            dataIndex: 'report_state',
            render: (_, record: DDRDisplayData) => (
                <Space size="middle">
                    {record.report_state % 2 === 0 ? (
                        <>
                            <Button color="geekblue" variant="text" onClick={() => viewFeedback(record.pid)}>
                                View Feedback
                            </Button>
                            <Button color="geekblue" variant="text" onClick={() => handleEditing(record)}>
                                Edit
                            </Button>
                            <Button color="geekblue" variant="text" onClick={() => passDDR2Next(record.pid, true)}>
                                Submit
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button color="geekblue" variant="text" onClick={() => handleEditing(record)}>
                                View
                            </Button>
                            <Button color="geekblue" variant="text" onClick={() => passDDR2Next(record.pid, true)}>
                                Approve
                            </Button>
                            <Button color="geekblue" variant="text" onClick={() => passDDR2Next(record.pid, false)}>
                                Reject
                            </Button>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="ddr_2_approve">
            {authorityLv === '' ? (
                <div className="inaccessible_state">
                    <img className="display_img" src={notLoginState} alt="Not signed in" />
                </div>
            ) : (
                <div className="table-wrapper">
                    <Table<DDRDisplayData> columns={columns} pagination={false} dataSource={data || []} />
                </div>
            )}
            <Modal
                title={isApproveAction ? "Approve DDR" : "Reject DDR"}
                open={isModalOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                okText={isApproveAction ? "Approve" : "Reject"}
                confirmLoading={isSubmitting}
            >
                <p>
                    {isApproveAction
                    ? 'This report will be uploaded to the database by following the workflow after approval.'
                    : 'This report will be rolled back after reject.'}
                </p>

                {!isApproveAction && (
                    <TextArea
                        showCount
                        rows={3}
                        onChange={rejectMsgOnChange}
                        placeholder="Leaving a rejection reason is helpful to understand what to fix :)"
                        style={{ marginBottom: 24, resize: 'none' }}
                    />
                )}
            </Modal>
        </div>
    );
};
